import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Grid3x3, X, Users, TrendingUp, Target } from 'lucide-react';
import { atlasApi, AtlasUser, NoveBoxResultado, PessoaNoveBox, Sucessor } from '../shared/atlasApi';
import AvatarColaborador from '../shared/AvatarColaborador';

interface Props {
  user: AtlasUser;
  podeEditar: boolean;
}

// Ordem visual da grade 3x3: desempenho (linhas, alto→baixo) x potencial (colunas, baixo→alto).
const ORDEM = [7, 8, 9, 4, 5, 6, 1, 2, 3];

const NIVEL_PRONTIDAO: Record<string, { label: string; cor: string }> = {
  pronto: { label: 'Pronto agora', cor: 'bg-emerald-100 text-emerald-700' },
  curto_prazo: { label: 'Curto prazo', cor: 'bg-amber-100 text-amber-700' },
  desenvolvimento: { label: 'Em desenvolvimento', cor: 'bg-gray-100 text-gray-600' },
};

const SucessaoApp: React.FC<Props> = ({ user }) => {
  const [dados, setDados] = useState<NoveBoxResultado | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [departamento, setDepartamento] = useState('');
  const [departamentos, setDepartamentos] = useState<string[]>([]);

  const [alvo, setAlvo] = useState<PessoaNoveBox | null>(null);
  const [sucessores, setSucessores] = useState<Sucessor[]>([]);
  const [loadingSuc, setLoadingSuc] = useState(false);

  const carregar = useCallback(() => {
    setLoading(true);
    setErro(null);
    atlasApi.getNoveBox(user, departamento || undefined)
      .then(r => {
        setDados(r);
        if (!departamento) {
          const deps = new Set<string>();
          Object.values(r.celulas).forEach(c => c.pessoas.forEach(p => p.departamento && deps.add(p.departamento)));
          r.sem_avaliacao.forEach(p => p.departamento && deps.add(p.departamento));
          setDepartamentos(Array.from(deps).sort());
        }
      })
      .catch(e => setErro(e.message || 'Erro ao carregar matriz'))
      .finally(() => setLoading(false));
  }, [user, departamento]);

  useEffect(() => { carregar(); }, [carregar]);

  const abrirSucessores = (p: PessoaNoveBox) => {
    setAlvo(p);
    setLoadingSuc(true);
    setSucessores([]);
    atlasApi.getSucessores(user, p.funccodi)
      .then(r => setSucessores(r.candidatos || []))
      .catch(() => setSucessores([]))
      .finally(() => setLoadingSuc(false));
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4 text-sm text-gray-500">
          <span className="inline-flex items-center gap-1"><Target className="w-4 h-4" /> Eixo horizontal: <b className="text-gray-700">potencial</b></span>
          <span className="inline-flex items-center gap-1"><TrendingUp className="w-4 h-4" /> Eixo vertical: <b className="text-gray-700">desempenho</b></span>
        </div>
        <select value={departamento} onChange={e => setDepartamento(e.target.value)} className="border border-gray-200 rounded-xl px-3 py-1.5 text-sm">
          <option value="">Todos os departamentos</option>
          {departamentos.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : erro ? (
        <FeedbackErro msg={erro} />
      ) : dados ? (
        <>
          <div className="flex items-center gap-3 text-xs text-gray-400">
            <span>{dados.avaliados} avaliados de {dados.total}</span>
            {dados.sem_avaliacao.length > 0 && <span>· {dados.sem_avaliacao.length} sem avaliação</span>}
          </div>

          <div className="grid grid-cols-3 gap-3">
            {ORDEM.map(cod => {
              const cel = dados.celulas[String(cod)];
              if (!cel) return <div key={cod} />;
              return (
                <div
                  key={cod}
                  className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden min-h-[160px] flex flex-col"
                >
                  <div className="px-3 py-2 flex items-center gap-2" style={{ background: cel.cor + '18' }}>
                    <span className="w-6 h-6 rounded-lg flex items-center justify-center text-xs font-bold text-white" style={{ background: cel.cor }}>{cel.cod}</span>
                    <span className="text-sm font-semibold text-gray-800">{cel.nome}</span>
                    <span className="ml-auto text-xs text-gray-400">{cel.pessoas.length}</span>
                  </div>
                  <div className="p-2 flex-1 space-y-1 overflow-y-auto max-h-44">
                    {cel.pessoas.length === 0 ? (
                      <div className="text-center text-gray-300 text-xs py-4">—</div>
                    ) : cel.pessoas.map(p => (
                      <button
                        key={p.funccodi}
                        onClick={() => abrirSucessores(p)}
                        className="flex items-center gap-2 w-full px-2 py-1 rounded-lg hover:bg-purple-50/60 text-left"
                      >
                        <AvatarColaborador nome={p.nome} fotoUrl={p.tem_foto ? atlasApi.fotoUrl(user, p.funccodi) : undefined} size={26} />
                        <div className="min-w-0">
                          <div className="text-xs font-medium text-gray-900 truncate">{p.nome}</div>
                          <div className="text-[10px] text-gray-400 truncate">{p.cargo || '—'}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {dados.sem_avaliacao.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Sem avaliação de desempenho ({dados.sem_avaliacao.length})</h4>
              <div className="flex flex-wrap gap-2">
                {dados.sem_avaliacao.map(p => (
                  <span key={p.funccodi} className="inline-flex items-center gap-1.5 bg-gray-50 border border-gray-100 rounded-full px-2 py-1 text-xs text-gray-600">
                    <AvatarColaborador nome={p.nome} fotoUrl={p.tem_foto ? atlasApi.fotoUrl(user, p.funccodi) : undefined} size={20} />
                    {p.nome}
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      ) : null}

      {alvo && (
        <div className="fixed inset-0 z-[10050] bg-black/50 flex items-center justify-end">
          <div className="bg-white w-full max-w-md h-full shadow-xl overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2"><Users className="w-5 h-5 text-purple-600" /> Mapa de sucessão</h3>
              <button onClick={() => setAlvo(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex items-center gap-3 bg-purple-50 border border-purple-100 rounded-xl px-3 py-3">
                <AvatarColaborador nome={alvo.nome} fotoUrl={alvo.tem_foto ? atlasApi.fotoUrl(user, alvo.funccodi) : undefined} size={44} />
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900 truncate">{alvo.nome}</div>
                  <div className="text-xs text-gray-500 truncate">{alvo.cargo || '—'} · {alvo.departamento || '—'}</div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    Desempenho {alvo.desempenho ?? '—'} · Potencial {alvo.potencial}
                  </div>
                </div>
              </div>

              <div className="text-sm font-semibold text-gray-700">Possíveis sucessores</div>
              {loadingSuc ? (
                <div className="flex items-center justify-center py-8 text-gray-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
              ) : sucessores.length === 0 ? (
                <div className="text-center py-8 text-gray-400 text-sm">Nenhum sucessor no mesmo departamento ainda.</div>
              ) : (
                <div className="space-y-2">
                  {sucessores.map(s => {
                    const np = NIVEL_PRONTIDAO[s.nivel_prontidao];
                    return (
                      <div key={s.funccodi} className="flex items-center gap-3 border border-gray-100 rounded-xl px-3 py-2">
                        <AvatarColaborador nome={s.nome} fotoUrl={s.tem_foto ? atlasApi.fotoUrl(user, s.funccodi) : undefined} size={36} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{s.nome}</div>
                          <div className="text-xs text-gray-400 truncate">{s.cargo || '—'}</div>
                        </div>
                        <div className="text-right">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${np.cor}`}>{np.label}</span>
                          <div className="text-xs text-gray-400 mt-0.5">prontidão {s.prontidao}%</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const FeedbackErro: React.FC<{ msg: string }> = ({ msg }) => (
  <div className="bg-rose-50 text-rose-700 border border-rose-200 rounded-lg px-3 py-2 text-sm">{msg}</div>
);

export default SucessaoApp;
